import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Phone, Mail, Clock, Printer, MapPin } from "lucide-react";
import { Reveal } from "@/components/site/reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_IMG = "/contact-caregiver.jpg";

const SERVICES = ["Personal Care", "Companionship", "Respite Care", "Dementia & Memory Care", "Not sure yet"];

const inputCls =
  "w-full bg-white border border-silver px-5 py-4 text-sm text-navy-dark placeholder:text-navy-dark/40 focus:outline-none focus:border-navy transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you — your enquiry is on its way. We respond within one business day.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong. Please try again or call us.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-white text-navy-dark border-t border-silver py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <Reveal>
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-12 bg-navy" />
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-navy">Get in touch</span>
            </div>
            <h2 className="font-display font-light tracking-tight leading-[1.05] text-4xl md:text-6xl">
              Start the <em className="italic text-navy">conversation</em>
            </h2>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-navy-dark/70 max-w-md">
              Tell us a little about your loved one and what you're hoping for. Karen or a
              member of her team will call you back within one business day — no pressure,
              no obligation.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="mt-12 space-y-5">
            <a data-testid="contact-phone-link" href="tel:+12484975650" className="flex items-center gap-4 text-navy-dark/80 hover:text-navy transition-colors duration-300">
              <Phone className="w-4 h-4 text-navy" />
              <span className="text-sm tracking-wide">248-497-5650</span>
            </a>
            <a data-testid="contact-email-link" href="mailto:Care@karenscaringhands.com" className="flex items-center gap-4 text-navy-dark/80 hover:text-navy transition-colors duration-300">
              <Mail className="w-4 h-4 text-navy" />
              <span className="text-sm tracking-wide">Care@karenscaringhands.com</span>
            </a>
            <div className="flex items-center gap-4 text-navy-dark/80">
              <Printer className="w-4 h-4 text-navy" />
              <span className="text-sm tracking-wide">Fax: 248-509-6050</span>
            </div>
            <div className="flex items-center gap-4 text-navy-dark/80">
              <Clock className="w-4 h-4 text-navy" />
              <span className="text-sm tracking-wide">Office hours: Mon–Sat, 8am–6pm · Care available 24/7</span>
            </div>
            <div className="flex items-center gap-4 text-navy-dark/80">
              <MapPin className="w-4 h-4 text-navy" />
              <span className="text-sm tracking-wide">Proudly serving Oakland County & surrounding areas</span>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="mt-12">
            <div className="relative overflow-hidden aspect-[16/10] max-w-md">
              <img
                data-testid="contact-image"
                src={CONTACT_IMG}
                alt="A caregiver in a navy blue uniform gently holding a senior client's hands"
                className="w-full h-full object-cover"
              />
              <div aria-hidden className="absolute inset-0 shadow-[inset_0_0_80px_rgba(16,43,85,0.45)]" />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="bg-white text-navy-dark border border-silver shadow-[0_30px_60px_-30px_rgba(16,43,85,0.2)] p-8 md:p-12 space-y-6"
          >
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-navy-dark/50">
              Enquiry form
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <input
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                className={inputCls}
              />
              <input
                data-testid="contact-email-input"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="Email address"
                className={inputCls}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <input
                data-testid="contact-phone-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder="Phone (optional)"
                className={inputCls}
              />
              <select
                data-testid="contact-service-select"
                value={form.service}
                onChange={set("service")}
                className={`${inputCls} appearance-none`}
              >
                <option value="">Which service?</option>
                {SERVICES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <textarea
              data-testid="contact-message-input"
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder="Tell us about your loved one and what kind of help you're looking for…"
              className={`${inputCls} resize-none`}
            />
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="group w-full inline-flex items-center justify-center gap-3 bg-navy text-white text-xs font-bold uppercase tracking-[0.2em] px-8 py-5 hover:bg-navy-dark transition-colors duration-300 disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send enquiry"}
              {!sending && (
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              )}
            </button>
            <p className="text-xs text-navy-dark/50 leading-relaxed">
              Your details are only ever used to respond to your enquiry. We never share them.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
